import axios from "axios";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

function getCookie(name) {
  const m = document.cookie.match("(^|;)\\s*" + name + "\\s*=\\s*([^;]+)");
  return m ? decodeURIComponent(m.pop()) : "";
}

// Double-submit CSRF: attach the csrf_token cookie as a header on state-changing requests.
api.interceptors.request.use((config) => {
  const method = (config.method || "get").toLowerCase();
  if (["post", "put", "patch", "delete"].includes(method)) {
    const token = getCookie("csrf_token");
    if (token) config.headers["X-CSRF-Token"] = token;
  }
  return config;
});

// Silent session refresh: on a 401 (expired access token), refresh once and retry.
// Single-flight so concurrent requests share one /auth/refresh call.
let refreshPromise = null;
api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;
    const url = original?.url || "";
    const isAuthPath =
      url.includes("/auth/refresh") || url.includes("/auth/login") || url.includes("/auth/logout");
    if (status === 401 && original && !original._retry && !isAuthPath) {
      original._retry = true;
      try {
        if (!refreshPromise) {
          refreshPromise = api.post("/auth/refresh").finally(() => { refreshPromise = null; });
        }
        await refreshPromise;
        return api(original);
      } catch {
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

export function mediaUrl(url) {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  return `${process.env.REACT_APP_BACKEND_URL}${url}`;
}

export function apiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// Shared image upload. Lets axios set the multipart boundary and surfaces the real server error.
export async function uploadFile(file) {
  const fd = new FormData();
  fd.append("file", file);
  try {
    const { data } = await api.post("/upload", fd);
    return data.url;
  } catch (err) {
    const detail = err?.response?.data?.detail;
    if (detail) throw new Error(apiError(detail));
    if (err?.response?.status === 413) throw new Error("That photo is too large (max 15MB). Please pick a smaller one.");
    throw new Error("Upload failed — check your connection and try again.");
  }
}
